import { useEffect, useState } from 'react';
import { api, type ProvenanceEntry } from '../api.ts';
import { Icon } from './Icon.tsx';

/**
 * Цепочка предыдущих владельцев (ТЗ 5.9, карточка 4.4, пункт 8).
 *
 * Показывается, только если заполнена: пустой блок «история до вас» в
 * карточке камня, купленного на выставке, — это шум. Завести цепочку
 * можно кнопкой, она рядом с блоком происхождения.
 */
export function Provenance({ specimenId }: { specimenId: string }) {
  const [entries, setEntries] = useState<ProvenanceEntry[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ owner_name: string; period_note: string; note: string }[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.provenance(specimenId).then((r) => setEntries(r.entries));
  }, [specimenId]);

  if (entries === null) return null;

  function startEditing() {
    setDraft(
      (entries ?? []).map((e) => ({
        owner_name: e.owner_name,
        period_note: e.period_note ?? '',
        note: e.note ?? '',
      })),
    );
    if ((entries ?? []).length === 0) {
      setDraft([{ owner_name: '', period_note: '', note: '' }]);
    }
    setEditing(true);
  }

  async function save() {
    setBusy(true);
    const result = await api.saveProvenance(
      specimenId,
      draft
        .filter((row) => row.owner_name.trim())
        .map((row) => ({
          owner_name: row.owner_name.trim(),
          period_note: row.period_note.trim() || null,
          note: row.note.trim() || null,
        })),
    );
    setEntries(result.entries);
    setBusy(false);
    setEditing(false);
  }

  if (!editing && entries.length === 0) {
    return (
      <div className="chips">
        <button type="button" className="chip chip--plain" onClick={startEditing}>
          <Icon name="route" className="icon icon-xs" /> цепочка владельцев
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="label-key">История до вас</div>

      {!editing ? (
        <>
          <ol className="chain">
            {entries.map((entry) => (
              <li key={entry.id}>
                <span className="chain-owner">{entry.owner_name}</span>
                {entry.period_note && <span className="hint">{entry.period_note}</span>}
                {entry.note && <span className="hint">{entry.note}</span>}
              </li>
            ))}
          </ol>
          <div className="chips">
            <button type="button" className="chip chip--plain" onClick={startEditing}>
              изменить цепочку
            </button>
          </div>
        </>
      ) : (
        <div className="valuation-form">
          {draft.map((row, index) => (
            <div key={index} className="chain-edit">
              <input
                className="field-input"
                placeholder={index === 0 ? 'самый ранний известный владелец' : 'следующий владелец'}
                value={row.owner_name}
                onChange={(e) =>
                  setDraft((d) => d.map((r, i) => (i === index ? { ...r, owner_name: e.target.value } : r)))
                }
              />
              <input
                className="field-input"
                style={{ marginTop: 'var(--space-2)' }}
                placeholder="период: до 1998, 1998–2014"
                value={row.period_note}
                onChange={(e) =>
                  setDraft((d) => d.map((r, i) => (i === index ? { ...r, period_note: e.target.value } : r)))
                }
              />
              <input
                className="field-input"
                style={{ marginTop: 'var(--space-2)' }}
                placeholder="заметка (необязательно)"
                value={row.note}
                onChange={(e) =>
                  setDraft((d) => d.map((r, i) => (i === index ? { ...r, note: e.target.value } : r)))
                }
              />
              <button
                type="button"
                className="link-btn link-btn--quiet"
                onClick={() => setDraft((d) => d.filter((_, i) => i !== index))}
              >
                убрать
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn--full"
            style={{ marginTop: 'var(--space-2)' }}
            onClick={() => setDraft((d) => [...d, { owner_name: '', period_note: '', note: '' }])}
          >
            <Icon name="plus" className="icon icon-sm" /> Ещё владелец
          </button>

          <div className="actions" style={{ marginTop: 'var(--space-3)' }}>
            <button className="btn" onClick={() => setEditing(false)}>
              Отмена
            </button>
            <button className="btn btn--primary" disabled={busy} onClick={save}>
              Сохранить
            </button>
          </div>
          <div className="hint centered">
            Порядок — от самого раннего владельца к последнему перед вами
          </div>
        </div>
      )}
    </>
  );
}
